import React from "react";
import {Box, Button, Divider, Drawer, IconButton, Typography} from "@mui/material";
import {
    AddLinkOutlined,
    CheckCircleOutlined,
    CheckOutlined,
    CloseOutlined,
    HelpOutlineOutlined,
    LinkOffOutlined,
    RadioButtonCheckedOutlined,
    ReplayOutlined,
    SwapHorizOutlined,
} from "@mui/icons-material";
import type {ClContract, ClSignalWeight} from "../contractLinkageData";
import {CL_CURRENCY} from "../contractLinkageData";
import type {ClReviewTx} from "../contractLinkageUtils";
import {
    BASIS_LABEL,
    CHANNEL_LABEL,
    formatCompactCurrency,
    formatDate,
    formatFullCurrency,
    formatPeriod,
    tierColors,
    tierOfConfidence,
} from "../contractLinkageUtils";
import {BasisChip, StatusChip} from "./LinkageCells";

type Props = {
    tx: ClReviewTx | null;
    contractsById: ReadonlyMap<string, ClContract>;
    onAccept: (txId: string) => void;
    /** Opens the contract picker for this transaction. */
    onAssign: (txId: string) => void;
    /** Direct assignment, used by the "alternatives considered" list. */
    onAssignTo: (txId: string, contractId: string) => void;
    onUnlink: (txId: string) => void;
    /** Clears the user decision, restoring the AI suggestion. */
    onReset: (txId: string) => void;
    onClose: () => void;
};

const SIGNAL_ICON: Record<ClSignalWeight, React.ReactNode> = {
    strong: <CheckCircleOutlined fontSize="small" className="cl-signal-icon is-strong"/>,
    medium: <RadioButtonCheckedOutlined fontSize="small" className="cl-signal-icon is-medium"/>,
    weak: <HelpOutlineOutlined fontSize="small" className="cl-signal-icon is-weak"/>,
};

const Fact: React.FC<{label: string; value: React.ReactNode}> = ({label, value}) => (
    <Box className="cl-drawer-fact">
        <Typography className="cl-drawer-fact-label">{label}</Typography>
        <Typography className="cl-drawer-fact-value">{value}</Typography>
    </Box>
);

export const TransactionDrawer: React.FC<Props> = ({tx, contractsById, onAccept, onAssign, onAssignTo, onUnlink, onReset, onClose}) => {
    const aiContract = tx?.aiContractId ? contractsById.get(tx.aiContractId) : null;
    const effectiveContract = tx?.effectiveContractId ? contractsById.get(tx.effectiveContractId) : null;
    const hasDecision = tx ? tx.status !== 'pending' && tx.status !== 'unmatched' : false;
    const tier = tx ? tierColors(tierOfConfidence(tx.confidence)) : null;

    return (
        // The app header is pinned at theme.zIndex.drawer + 1, so lift above it
        <Drawer anchor="right" open={!!tx} onClose={onClose}
                sx={theme => ({zIndex: theme.zIndex.drawer + 2})}>
            {tx && <Box className="cl-drawer">
                <Box className="cl-drawer-header">
                    <Box>
                        <Typography variant="h2" className="cl-drawer-title">{tx.description}</Typography>
                        <Box className="cl-drawer-subrow">
                            <StatusChip status={tx.status}/>
                            <span className="cl-drawer-subtext">{tx.supplier} · {tx.docNumber}</span>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small"><CloseOutlined/></IconButton>
                </Box>

                <Box className="cl-drawer-facts">
                    <Fact label="Amount" value={formatFullCurrency(tx.amount, CL_CURRENCY)}/>
                    <Fact label="Invoice date" value={formatDate(tx.date)}/>
                    <Fact label="Posting date" value={formatDate(tx.postingDate)}/>
                    <Fact label="Cleared" value={tx.clearingDate ? formatDate(tx.clearingDate) : 'Open'}/>
                    <Fact label="Entity" value={tx.entity}/>
                    <Fact label="Cost centre" value={tx.costCenter}/>
                    <Fact label="Category" value={tx.category}/>
                    <Fact label="Source" value={tx.channel ? CHANNEL_LABEL[tx.channel] : 'Direct invoice'}/>
                </Box>

                <Divider sx={{my: 2}}/>

                {/* What the transaction is effectively linked to right now */}
                <Typography className="cl-drawer-section">
                    {tx.status === 'relinked' ? 'Linked contract (your decision)' : 'Linked contract'}
                </Typography>
                {effectiveContract ? (
                    <Box className="cl-drawer-contract">
                        <Typography className="cl-drawer-contract-name">{effectiveContract.name}</Typography>
                        <Typography className="cl-drawer-contract-meta">
                            {effectiveContract.type} · {formatPeriod(effectiveContract)}{' · '}
                            {formatCompactCurrency(effectiveContract.contractedValue, CL_CURRENCY)}{' · '}
                            {effectiveContract.paymentTerms}
                        </Typography>
                        <Typography className="cl-drawer-contract-scope">{effectiveContract.scopeSummary}</Typography>
                    </Box>
                ) : (
                    <Box className="cl-drawer-nolink">
                        {tx.status === 'unlinked'
                            ? 'You removed the AI link — this transaction is currently not allocated to any contract.'
                            : 'The AI did not link this transaction to a contract.'}
                    </Box>
                )}

                {/* Why the AI proposed its match */}
                {aiContract && (
                    <>
                        <Typography className="cl-drawer-section">
                            AI match evidence
                            {tx.basis && <span className="cl-drawer-section-side"><BasisChip basis={tx.basis}/></span>}
                        </Typography>
                        {tx.status === 'relinked' && (
                            <Typography className="cl-drawer-ai-note">
                                The AI suggested <b>{aiContract.name}</b> ({BASIS_LABEL[tx.basis!]}):
                            </Typography>
                        )}
                        <Box className="cl-drawer-gauge">
                            <Box className="cl-drawer-gauge-track">
                                <Box className="cl-drawer-gauge-fill"
                                     style={{width: `${tx.confidence}%`, backgroundColor: tier!.fg}}/>
                            </Box>
                            <span className="cl-drawer-gauge-label" style={{color: tier!.fg}}>
                                {tx.confidence}% confidence
                            </span>
                        </Box>
                    </>
                )}
                {!aiContract && tx.signals.length > 0 && (
                    <Typography className="cl-drawer-section">Why the AI found no match</Typography>
                )}
                <Box className="cl-drawer-signals">
                    {tx.signals.map(sig => (
                        <Box key={sig.label} className="cl-drawer-signal">
                            {SIGNAL_ICON[sig.weight]}
                            <Box>
                                <Typography className="cl-drawer-signal-label">{sig.label}</Typography>
                                <Typography className="cl-drawer-signal-detail">{sig.detail}</Typography>
                            </Box>
                        </Box>
                    ))}
                </Box>

                {tx.alternatives.length > 0 && (
                    <>
                        <Typography className="cl-drawer-section">Also considered</Typography>
                        <Box className="cl-drawer-alts">
                            {tx.alternatives.map(alt => {
                                const c = contractsById.get(alt.contractId);
                                if (!c) return null;
                                const altTier = tierColors(tierOfConfidence(alt.confidence));
                                const isCurrent = tx.effectiveContractId === c.id;
                                return (
                                    <Box key={alt.contractId} className="cl-drawer-alt">
                                        <Box className="cl-drawer-alt-main">
                                            <Typography className="cl-drawer-alt-name">{c.name}</Typography>
                                            <Typography className="cl-drawer-alt-meta">
                                                {c.type} · {formatPeriod(c)}
                                            </Typography>
                                        </Box>
                                        <span className="cl-drawer-alt-conf" style={{color: altTier.fg}}>
                                            {alt.confidence}%
                                        </span>
                                        <Button size="small" variant="outlined" disabled={isCurrent}
                                                onClick={() => onAssignTo(tx.id, c.id)}>
                                            {isCurrent ? 'Linked' : 'Use'}
                                        </Button>
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                )}

                <Divider sx={{my: 2}}/>

                <Typography className="cl-drawer-section">Your decision</Typography>
                <Box className="cl-drawer-actions">
                    {tx.aiContractId && tx.status !== 'accepted' && (
                        <Button variant="contained" size="small" startIcon={<CheckOutlined/>}
                                onClick={() => onAccept(tx.id)}>
                            Accept AI link
                        </Button>
                    )}
                    <Button variant="outlined" size="small"
                            startIcon={tx.effectiveContractId ? <SwapHorizOutlined/> : <AddLinkOutlined/>}
                            onClick={() => onAssign(tx.id)}>
                        {tx.effectiveContractId ? 'Move to another contract…' : 'Link to a contract…'}
                    </Button>
                    {tx.effectiveContractId && (
                        <Button variant="outlined" size="small" color="error" startIcon={<LinkOffOutlined/>}
                                onClick={() => onUnlink(tx.id)}>
                            Remove link
                        </Button>
                    )}
                    {hasDecision && (
                        <Button variant="text" size="small" startIcon={<ReplayOutlined/>}
                                onClick={() => onReset(tx.id)}>
                            Reset to AI suggestion
                        </Button>
                    )}
                </Box>
            </Box>}
        </Drawer>
    );
};
